import type { DateTimeFormatType } from "@/context/UserContext"

// Money number formatting follows the user's region toggle (window.__USER__.dateTimeFormat,
// the same signal used for dates) — there is no dedicated number-format preference.
//   usa    → "1,234.56"  (comma thousands, dot decimal)
//   europe → "1 234,56"  (space thousands, comma decimal)
// Default is "europe" unless the setting is explicitly "usa" — mirrors UserContext.
export type MoneyFormat = DateTimeFormatType

export function resolveMoneyFormat(): MoneyFormat {
  if (typeof window !== "undefined" && window.__USER__?.dateTimeFormat === "usa") {
    return "usa"
  }
  return "europe"
}

interface MoneyFormatOptions {
  fractionDigits?: number
  /** Override the resolved user format (mainly for tests). */
  format?: MoneyFormat
}

/** Format a numeric value: "1,234.56" (usa) or "1 234,56" (europe). */
export function formatMoneyValue(value: number, opts: MoneyFormatOptions = {}): string {
  const format = opts.format ?? resolveMoneyFormat()
  const fractionDigits = opts.fractionDigits ?? 2
  const negative = value < 0
  const [intPart, decPart] = Math.abs(value).toFixed(fractionDigits).split(".")
  const groupSep = format === "usa" ? "," : " "
  const decSep = format === "usa" ? "." : ","
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, groupSep)
  const body = decPart ? `${grouped}${decSep}${decPart}` : grouped
  return negative ? `-${body}` : body
}

/** Format a value with its currency code: "1 234,56 USD". */
export function formatMoneyAmount(value: number, currency: string, opts: MoneyFormatOptions = {}): string {
  return `${formatMoneyValue(value, opts)} ${currency}`
}

/**
 * A number out of what somebody typed OR PASTED into a money field.
 *
 * The figures on the order card are rendered with separators — «1,027.55» in
 * the usa format, «1 027,55» in the europe one — and the first thing an
 * operator does is copy one column into another. Sending that string on was
 * refused by the server, which is a correct answer to a question nobody meant
 * to ask: the value is unambiguous to a reader and should be to us.
 *
 * Which character is the DECIMAL point is decided per string, not per locale,
 * because a pasted value may come from either format:
 *
 *  - both `,` and `.` present → the LAST one is the decimal point, the other
 *    groups thousands («1,027.55» and «1.027,55» are both 1027.55);
 *  - one kind, repeated → they all group thousands («1,027,500» = 1027500);
 *  - a single `,` with exactly three digits after it → thousands («1,027»),
 *    because money written to three decimals is not what that is;
 *  - any other single separator → the decimal point («1027,55», «1027.5»).
 *
 * Returns null for anything that is not a number, so a caller can refuse
 * rather than send NaN.
 */
export function parseMoneyInput(text: string): number | null {
  const cleaned = text.replace(/[\s  ']/g, "")
  if (cleaned === "" || !/^-?[\d.,]+$/.test(cleaned)) return null

  const lastComma = cleaned.lastIndexOf(",")
  const lastDot = cleaned.lastIndexOf(".")
  let decimalAt = -1

  if (lastComma >= 0 && lastDot >= 0) {
    decimalAt = Math.max(lastComma, lastDot)
  } else if (lastComma >= 0) {
    const onlyOne = cleaned.indexOf(",") === lastComma
    const groupsThousands = onlyOne && /,\d{3}$/.test(cleaned)
    decimalAt = onlyOne && !groupsThousands ? lastComma : -1
  } else if (lastDot >= 0) {
    decimalAt = cleaned.indexOf(".") === lastDot ? lastDot : -1
  }

  const whole = (decimalAt < 0 ? cleaned : cleaned.slice(0, decimalAt)).replace(/[.,]/g, "")
  const fraction = decimalAt < 0 ? "" : cleaned.slice(decimalAt + 1).replace(/[.,]/g, "")
  const value = Number(fraction === "" ? whole : `${whole}.${fraction}`)

  return Number.isFinite(value) ? value : null
}

/**
 * The same, for a field that is NOT money — a currency rate.
 *
 * A rate is written to four or five decimals, so «1,027» there means 1.027 and
 * the thousands rule above would read it as a thousand and twenty-seven. Rates
 * do not reach four digits, so there is nothing for a thousands separator to
 * do: a single separator of either kind is always the decimal point.
 */
export function parseRateInput(text: string): number | null {
  const cleaned = text.replace(/[\s  ']/g, "").replace(",", ".")

  if (cleaned === "" || !/^-?\d*\.?\d+$/.test(cleaned)) return null
  const value = Number(cleaned)

  return Number.isFinite(value) ? value : null
}
